import { type JSX, useId, useState } from 'react';
import { type FooterLevelOneGroup } from '@cx-web/domain-types';
import { trackEvent } from '@cx-web/feature-tracking/dataLayer';
import { normalizeSchemeHref } from '@cx-web/utils/formatLinkProperties';
import { useToggle } from '~/vendor/cxy/components/Accordion';
import classnames from 'classnames';

import FooterNavGroupTitle from './components/FooterNavGroupTitle/index';
import styles from './FooterNavGroup.module.scss';

const Sublink = ({ link, name }: { link: string; name: string }) => {
  return (
    <li className={styles.subLinkItem}>
      <a
        className={styles.subLink}
        href={normalizeSchemeHref(link)}
        onClick={() => trackEvent('footer_level_one_nav', { selection: name })}
        title={name}
      >
        {name}
      </a>
    </li>
  );
};

type FooterNavGroupProps = FooterLevelOneGroup & {
  isUkFooterStyle?: boolean;
};

export const FooterNavGroup = ({ isUkFooterStyle, name, subLinks }: FooterNavGroupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const subLinksId = useId();

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const sublinks = subLinks.map(({ link, name }) => <Sublink key={name} link={link} name={name} />);

  // The UK footer already renders a real button and a caret of its own, and its design specs measure
  // that caret. Only the other style is migrated here — it is the one that was a `div` with an
  // `onClick`, no `aria-expanded` and a `+`/`−` glyph.
  if (isUkFooterStyle) {
    return (
      <li
        className={classnames(styles.footerNavGroupWrapper, styles.footerNavGroupWrapperUk)}
        data-testid="footer-level-one-group"
      >
        <FooterNavGroupTitle isOpen={isOpen} isUkFooterStyle subLinksId={subLinksId} title={name} toggle={toggle} />
        <ul
          className={classnames(styles.footerNavSubLinks, styles.footerNavSubLinksUk, { [styles.open]: isOpen })}
          id={subLinksId}
        >
          {sublinks}
        </ul>
      </li>
    );
  }

  return <MigratedFooterNavGroup name={name} sublinks={sublinks} />;
};

/**
 * The cxy disclosure, through its HEADLESS hook rather than `Accordion.Item`.
 *
 * `Accordion.Item` hides a closed panel with the `hidden` ATTRIBUTE, and this footer is not a
 * disclosure at all above its desktop breakpoint: every group is open and there is no control, done
 * purely in CSS so that it works before hydration and with JS off. Rendering the component would mean
 * overriding `[hidden]` there — leaving a visible panel asserting that it is hidden — or driving
 * `isOpen` from a viewport hook, which trades the CSS-only behaviour for a hydration flash on an
 * SSR'd footer. The hook gives the chevron, `aria-expanded`, `aria-controls` and `data-state` while
 * the panel stays the footer's own `<ul>` with the `max-height` transition it already had.
 *
 * Only the id pair is taken off `sectionProps`, and named rather than spread. The rest is meant for
 * the `<div>` the component renders: `hidden` is the part this panel must not have, and `role`
 * ("region") is invalid on a `<ul>` — it also takes the list role away from the `<li>`s inside it,
 * which axe reports twice.
 */
const MigratedFooterNavGroup = ({ name, sublinks }: { name: string; sublinks: JSX.Element[] }) => {
  // `isOpen: false` explicitly. The hook leaves its open state `undefined` when nothing is passed,
  // and React drops an attribute whose value is `undefined` — so the trigger renders with NO
  // `aria-expanded` at all, which is the defect this migration exists to remove. `Accordion.Item`
  // defaults it; the bare hook does not.
  const { Indicator, headingProps, sectionProps, triggerClass } = useToggle({ isOpen: false });
  const { 'aria-labelledby': labelledBy, hidden, id: panelId } = sectionProps;

  return (
    <li className={styles.footerNavGroupWrapper} data-testid="footer-level-one-group">
      <div className={styles.footerNavGroupHeader}>
        <button {...headingProps} className={classnames(triggerClass, styles.footerNavGroupTrigger)} type="button">
          <span className={styles.footerNavGroupTitleText}>{name}</span>
          <Indicator />
        </button>
      </div>
      {/* Before the list, not after: on desktop the trigger above is display:none and this is the
          group's heading, so DOM order is what puts it above its links. */}
      <p className={styles.footerNavGroupTitleDesktop}>{name}</p>
      <ul
        aria-labelledby={labelledBy}
        className={classnames(styles.footerNavSubLinks, { [styles.open]: !hidden })}
        id={panelId}
      >
        {sublinks}
      </ul>
    </li>
  );
};
